import { FC, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserRoleTable.translations.json';
import { Flex } from 'antd';
import { createColumnHelper } from '@tanstack/react-table';
import {
  TextEllipsis,
  UserRoleFormDataType,
  getDatePeriodString,
  sortPeriod,
  Table,
  TableProps,
} from '@atlas-frontend/sdk';
import { UiLink } from '../../paths/UiLink';
import { path } from '../../paths';

const { addTranslations, useTranslation } = i18nNsFactory('user-role-table');
addTranslations(translations);

const columnHelper = createColumnHelper<UserRoleFormDataType>();

export type UserRoleTableProps = Omit<
  TableProps<UserRoleFormDataType>,
  'data' | 'name' | 'columns'
> & {
  userRoles: UserRoleFormDataType[];
};

export const UserRoleViewTable: FC<UserRoleTableProps> = (props) => {
  const { userRoles } = props;
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('column.name'),
        meta: { width: '20ch', className: 'sticky-left' },
        enableColumnFilter: false,
        cell: (props) => (
          <TextEllipsis>
            <UiLink
              id={`user-role-name-${props.row.index}`}
              path={path.roleDetails}
              params={{ id: props.row.original.id }}
              target="_blank"
            >
              {props.renderValue()}
            </UiLink>
          </TextEllipsis>
        ),
      }),
      columnHelper.accessor(
        (rowData) =>
          rowData.permissions?.map((entity) => entity.name).join(', '),
        {
          id: 'permissions',
          header: t('column.permissions'),
          meta: { width: '60%' },
          enableColumnFilter: false,
        },
      ),
      columnHelper.accessor(
        (rowData) =>
          rowData.accessGroups?.map((entity) => entity.name).join(', '),
        {
          header: t('column.accessGroups'),
          meta: { width: '40%' },
          enableColumnFilter: false,
        },
      ),
      columnHelper.accessor('validPeriod', {
        header: t('column.validPeriod'),
        meta: { width: 180 },
        enableColumnFilter: false,
        cell: (props) => (
          <TextEllipsis>
            {getDatePeriodString(
              props.renderValue()?.[0],
              props.renderValue()?.[1],
            )}
          </TextEllipsis>
        ),
        sortingFn: (a, b) =>
          sortPeriod(a.original.validPeriod, b.original.validPeriod),
      }),
    ],
    [t],
  );

  return (
    <Flex align="end" gap="middle" vertical>
      <Table
        name="userRoles"
        data={userRoles}
        columns={columns}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </Flex>
  );
};
